import { Columns3 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { MenuGroup, MenuItem, Popover, usePopover } from '../Popover';
import { TableViews, type TableViewsConfig } from './TableViews';
import type { Column } from './types';
import type { TablePreferences } from './use-table-preferences';

interface ColumnsMenuProps<TRow> {
  readonly columns: readonly Column<TRow>[];
  readonly preferences: TablePreferences;
  readonly views?: TableViewsConfig;
}

/** The columns menu's contents (06-qol §3): saved views when the screen has them, every column, and the reset. */
export function ColumnsMenuItems<TRow>({ columns, preferences, views, onDone }: ColumnsMenuProps<TRow> & { readonly onDone: () => void }): ReactNode {
  return (
    <>
      {views && <TableViews config={views} hiddenColumns={[...preferences.hiddenColumns]} onApplyColumns={preferences.setHiddenColumns} onDone={onDone} />}
      <MenuGroup label="Columns">
        {columns.map((column) => {
          const isShown = !preferences.hiddenColumns.has(column.id);
          return (
            <MenuItem
              key={column.id}
              disabled={column.isIdentity === true}
              isSelected={isShown}
              onClick={() => preferences.toggleColumn(column.id)}
              icon={<Checkbox checked={isShown} onChange={() => preferences.toggleColumn(column.id)} ariaLabel={column.header} disabled={column.isIdentity === true} />}
            >
              {column.header}
            </MenuItem>
          );
        })}
      </MenuGroup>
      <MenuItem onClick={preferences.resetColumns}>Reset columns and widths</MenuItem>
    </>
  );
}

/**
 * The same menu behind a toolbar button, for a screen whose mockup puts it beside View (JOB-01,
 * Jobs.dc.html) rather than in the table's header. Pair with `DataTable isColumnMenuInHeader={false}`.
 */
export function ColumnsMenuButton<TRow>({ columns, preferences, views }: ColumnsMenuProps<TRow>): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const label = views ? 'Columns and saved views' : 'Choose columns';
  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" icon={Columns3} aria-label={label} title={label} aria-haspopup="menu" aria-expanded={isOpen} onClick={toggle} />
      <Popover anchor={anchor} onClose={close} align="right" label={label}>
        <ColumnsMenuItems columns={columns} preferences={preferences} views={views} onDone={close} />
      </Popover>
    </>
  );
}
